package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.RecurringSchedule
import com.dotdrives.repository.RecurringScheduleRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

data class RecurringSchedulesUiState(
    val items: List<RecurringSchedule> = emptyList(),
    val total: Int = 0,
    val status: String = "active",
    val query: String = "",
    val offset: Int = 0,
    val loading: Boolean = false,
    val error: String? = null,
) {
    val hasPrevious: Boolean get() = offset > 0
    val hasNext: Boolean get() = offset + RECURRING_PAGE_SIZE < total
}

const val RECURRING_PAGE_SIZE = 50

/** Bound to one mounted account/org/giver view. No request until explicitly loaded. */
class RecurringSchedulesController(
    private val repo: RecurringScheduleRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val giverId: String? = null,
) : DotController<RecurringSchedulesUiState>(RecurringSchedulesUiState(), scope) {
    private var generation = 0
    private var job: Job? = null

    fun load() = request()
    fun close() {
        generation++
        job?.cancel()
        update { RecurringSchedulesUiState() }
    }
    fun search(query: String) {
        if (query == state.value.query) return
        update { it.copy(query = query, offset = 0) }
        request(debounce = true)
    }
    fun filter(status: String) {
        require(status in setOf("active", "needs_review", "ended", "all"))
        if (status == state.value.status) return
        update { it.copy(status = status, offset = 0) }
        request()
    }
    fun previous() {
        if (!state.value.hasPrevious || state.value.loading) return
        update { it.copy(offset = (it.offset - RECURRING_PAGE_SIZE).coerceAtLeast(0)) }
        request()
    }
    fun next() {
        if (!state.value.hasNext || state.value.loading) return
        update { it.copy(offset = it.offset + RECURRING_PAGE_SIZE) }
        request()
    }
    private fun request(debounce: Boolean = false) {
        val request = ++generation
        job?.cancel()
        val parameters = state.value
        update { it.copy(loading = true, error = null, items = emptyList()) }
        job = scope.launch {
            try {
                if (debounce) delay(300)
                val page = repo.list(orgId, giverId, parameters.status, parameters.query, parameters.offset, RECURRING_PAGE_SIZE).getOrThrow()
                if (request == generation) update { it.copy(items = page.items, total = page.total, loading = false) }
            } catch (error: Throwable) {
                if (error is CancellationException) throw error
                if (request == generation) update { it.copy(loading = false, error = "Unable to load recurring schedules. Please try again.") }
            }
        }
    }
}
